import useFocusState from "@/hooks/useFocusState";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import DailyReportImportDialog from "@/components/DailyReportImportDialog";
import { TrendingUp, TrendingDown, Upload, FileText } from "lucide-react";

const EMPTY_ARRAY = [];

export default function Pulse() {
  const { user } = useAuth();
  const [district, setDistrict] = useState(null);
  const [tab, setTab] = useFocusState(user?.boutique_code || "district");
  const [boutique, setBoutique] = useState(null);
  const [reports, setReports] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => { api.get("/pulse/district").then((r) => setDistrict(r.data)); }, []);

  useEffect(() => {
    if (tab !== "district") api.get(`/pulse/boutique/${tab}`).then((r) => setBoutique(r.data));
  }, [tab]);

  const loadReports = useCallback(() => {
    if (tab === "district") { setReports([]); return; }
    api.get("/daily-reports", { params: { boutique: tab, limit: 30 } })
      .then((r) => setReports(r.data || []))
      .catch(() => setReports([]));
  }, [tab]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const canImport = user && tab !== "district" && (
    user.role === "owner" || user.role === "recruiter" ||
    (user.role === "boutique_manager" && user.boutique_code === tab)
  );
  const selectedName = tab === "district" ? "Rainbow · district view" : `${boutiqueOf(tab).short} · boutique view`;

  return (
    <div>
      <Topbar
        title="Pulse"
        context={selectedName}
        attention={tab !== "district" && !reports.length && boutique ? {
          tone: "info",
          label: "No daily report is available yet",
          detail: "Import the Zenoti end-of-day report when it arrives.",
        } : null}
        primaryAction={canImport ? (
          <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/25 bg-white/10 text-sm text-white hover:bg-white/20">
            <Upload size={14} strokeWidth={1.5} /> Import daily report
          </button>
        ) : null}
      />
      <div className="px-4 sm:px-10 pb-12 sm:pb-16 space-y-8 stagger-fade">
        <div className="flex items-center gap-3 flex-wrap justify-between" data-testid="pulse-tabs">
          <div className="sonmi-nav sonmi-nav--primary flex items-center gap-3 flex-wrap">
            <TabButton active={tab === "district"} onClick={() => setTab("district")} testId="tab-district">
              Rainbow — District
            </TabButton>
            {["PLT", "FLL", "WYN", "PNC"].map((c) => {
              const m = boutiqueOf(c);
              return (
                <TabButton key={c} active={tab === c} onClick={() => setTab(c)} testId={`tab-${c}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.dot} mr-2`} />
                  {m.short}
                </TabButton>
              );
            })}
          </div>
        </div>

        {tab === "district" && district && <DistrictView district={district} />}
        {tab !== "district" && boutique && <BoutiqueView boutique={boutique} />}
        {tab !== "district" && reports.length > 0 && <DailyReportsSection reports={reports} boutique={tab} />}
      </div>

      <DailyReportImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        defaultBoutique={tab !== "district" ? tab : null}
        onImported={loadReports}
      />
    </div>
  );
}

function TabButton({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`px-4 py-2 rounded-full text-sm transition-colors ${
        active ? "bg-stone-900 text-white" : "bg-white subtle-border text-stone-600 hover:bg-stone-50"
      }`}
    >
      {children}
    </button>
  );
}

function KpiCard({ label, value, delta, testId }) {
  const up = delta > 0; const down = delta < 0;
  return (
    <div className="bg-white subtle-border rounded-2xl p-8 card-shadow" data-testid={testId}>
      <div className="label-eyebrow">{label}</div>
      <div className="flex items-baseline gap-3 mt-4">
        <span className="text-4xl font-light text-stone-900">{value}</span>
        {delta !== undefined && delta !== 0 && (
          <span className={`text-xs flex items-center gap-0.5 ${up ? "text-emerald-600" : "text-rose-500"}`}>
            {up ? <TrendingUp size={12} strokeWidth={1.5} /> : <TrendingDown size={12} strokeWidth={1.5} />}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
    </div>
  );
}

function DistrictView({ district }) {
  const r = {
    revenue_week: 0,
    revenue_delta: 0,
    appointments_week: 0,
    appointments_delta: 0,
    rebook_rate: 0,
    rebook_delta: 0,
    nps: "—",
    nps_delta: 0,
    ...(district?.rainbow || district?.summary || district || {}),
  };
  const boutiques = Array.isArray(district?.boutiques) ? district.boutiques : [];
  return (
    <>
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Revenue this week" value={`$${(r.revenue_week / 1000).toFixed(1)}k`} delta={r.revenue_delta} testId="kpi-revenue" />
        <KpiCard label="Appointments" value={r.appointments_week.toLocaleString()} delta={r.appointments_delta} testId="kpi-appointments" />
        <KpiCard label="Avg rebook rate" value={`${r.rebook_rate}%`} delta={r.rebook_delta} testId="kpi-rebook" />
        <KpiCard label="Avg guest NPS" value={r.nps} delta={r.nps_delta} testId="kpi-nps" />
      </section>

      <section className="bg-white subtle-border rounded-2xl p-8 card-shadow">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif-display text-2xl text-stone-900">Across boutiques</h2>
          <span className="label-eyebrow">Sorted by revenue</span>
        </div>
        <div className="divide-y divide-stone-50">
          {boutiques.map((b) => {
            const meta = boutiqueOf(b.code);
            return (
              <div key={b.code} className={`grid grid-cols-2 lg:grid-cols-6 gap-4 items-center py-5 pl-4 ${meta.accent}`} data-testid={`row-boutique-${b.code}`}>
                <div className="lg:col-span-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    <span className="text-stone-800">{meta.short}</span>
                  </div>
                  <div className="text-xs text-stone-400 mt-1">{b.city}</div>
                </div>
                <StatBlock label="Revenue" value={`$${(b.revenue / 1000).toFixed(1)}k`} delta={b.revenue_delta} />
                <StatBlock label="Rebook" value={`${b.rebook_rate}%`} />
                <StatBlock label="NPS" value={b.nps} />
                <StatBlock label="New members" value={b.new_members} />
              </div>
            );
          })}
          {boutiques.length === 0 && (
            <div className="py-8 text-sm text-stone-400">Boutique pulse will appear as daily reports arrive.</div>
          )}
        </div>
      </section>
    </>
  );
}

function DailyReportsSection({ reports, boutique }) {
  const latest = reports[0];
  const mtd = latest?.mtd_sales || EMPTY_ARRAY;
  const maxTotal = useMemo(() => Math.max(1, ...mtd.map((d) => d.total || 0)), [mtd]);
  const meta = boutiqueOf(boutique);

  const fmt$ = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString()}`);
  const fmtN = (n) => (n == null ? "—" : n.toLocaleString());
  const fmtP = (n) => (n == null ? "—" : `${n}%`);
  const fmtDay = (iso) => {
    try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  return (
    <section className="space-y-6" data-testid="daily-reports-section">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-2">
          <FileText size={12} strokeWidth={1.5} className="text-stone-400" />
          Zenoti daily · {fmtDay(latest.date)}
        </div>
        <div className="text-[11px] text-stone-400">
          imported {latest.imported_by_name ? `by ${latest.imported_by_name}` : ""}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Revenue" value={fmt$(latest.sales?.total)} testId="drev" />
        <KpiCard label="Services" value={`${latest.sales?.services?.count ?? 0} · ${fmt$(latest.sales?.services?.amount)}`} testId="dsvc" />
        <KpiCard label="Memberships sold" value={fmtN(latest.sales?.memberships?.count)} testId="dmem" />
        <KpiCard label="Rebook rate" value={fmtP(latest.guests?.rebook_pct)} testId="dreb" />
        <KpiCard label="New guests" value={fmtN(latest.guests?.new_count)} testId="dng" />
        <KpiCard label="Existing guests" value={fmtN(latest.guests?.existing_count)} testId="deg" />
        <KpiCard label="Utilization" value={fmtP(latest.guests?.center_utilization_employee_pct)} testId="dutil" />
        <KpiCard label="Cancellations" value={fmtN(latest.guests?.cancellations)} testId="dcx" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white subtle-border rounded-2xl p-6 card-shadow">
          <div className="label-eyebrow mb-3">Top MTD therapist</div>
          <div className="font-serif-display text-2xl text-stone-900">{latest.employees?.top_service_therapist || "—"}</div>
          <div className="text-xs text-stone-400 mt-1">Lowest: {latest.employees?.low_service_therapist || "—"}</div>
        </div>
        <div className="bg-white subtle-border rounded-2xl p-6 card-shadow">
          <div className="label-eyebrow mb-3">Attendance today</div>
          <div className="flex items-baseline gap-4">
            <div><span className="text-2xl font-light text-stone-900">{fmtN(latest.employees?.on_time)}</span> <span className="text-xs text-stone-400">on-time</span></div>
            <div><span className="text-2xl font-light text-amber-700">{fmtN(latest.employees?.late)}</span> <span className="text-xs text-stone-400">late</span></div>
            <div><span className="text-2xl font-light text-rose-600">{fmtN(latest.employees?.no_show)}</span> <span className="text-xs text-stone-400">no-show</span></div>
          </div>
        </div>
      </div>

      {mtd.length > 1 && (
        <div className="bg-white subtle-border rounded-2xl p-6 card-shadow" data-testid="mtd-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow">Month-to-date daily revenue</div>
            <div className="text-[11px] text-stone-400">
              projected month: {fmt$(latest.revenue?.projected_monthly)}
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-32">
            {mtd.map((d) => {
              const h = Math.max(4, Math.round(((d.total || 0) / maxTotal) * 100));
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${fmtDay(d.date)} · ${fmt$(d.total)}`}>
                  <div className={`w-full rounded-t transition-colors ${meta.dot} opacity-70 group-hover:opacity-100`} style={{ height: `${h}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex gap-1.5 mt-1">
            {mtd.map((d) => (
              <div key={d.date + "-lbl"} className="flex-1 text-center text-[9px] text-stone-400">{new Date(d.date + "T00:00:00").getDate()}</div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function StatBlock({ label, value, delta }) {
  const up = delta > 0; const down = delta < 0;
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span className="text-lg font-light text-stone-800">{value}</span>
        {delta !== undefined && delta !== 0 && (
          <span className={`text-[10px] ${up ? "text-emerald-600" : "text-rose-500"}`}>
            {up ? "▲" : "▼"} {Math.abs(delta)}%
          </span>
        )}
      </div>
    </div>
  );
}

function BoutiqueView({ boutique }) {
  const b = boutique.boutique;
  const meta = boutiqueOf(b.code);
  return (
    <>
      <section className={`bg-white subtle-border rounded-2xl p-10 card-shadow ${meta.accent}`}>
        <div className="label-eyebrow flex items-center gap-2">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {b.code} · {b.city}
        </div>
        <h2 className="font-serif-display text-4xl text-stone-900 mt-3">{b.name}</h2>
        <div className="text-stone-500 mt-2 text-sm">{boutique.staff_count} teammates</div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {boutique.metrics.map((m) => (
          <KpiCard
            key={m.key}
            testId={`bkpi-${m.key}`}
            label={m.label}
            value={m.suffix === "$" ? `$${m.value.toLocaleString()}` : `${m.value.toLocaleString()}${m.suffix}`}
            delta={m.delta}
          />
        ))}
      </section>
    </>
  );
}
